import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Marquee } from '../components/marquee';
import VideoPlayer from '../components/videoPlayer3';
import { useHideMenu } from '../hooks/useHideMenu';
import { UseSweetAlert } from '../hooks/useSweetAlert';
import { SocketContext } from '../context/SocketContext';
import { getUltimos } from '../helpers/getUltimos';
import { parseVideosLista } from '../helpers/videosMedia';
import { serverApi } from '../api';
import sound from "../assets/new-ticket.mp3";
import { SelectElement } from '../components/selectElement';
import './CallScreen.css';
import { guardarColoresStorage } from '../helpers/theme';

// Popup de llamado: cuánto permanece visible cada tarjeta, ventana para
// agrupar llamados casi simultáneos (llegan con 1-2 s de diferencia) y
// máximo de tarjetas mostradas a la vez.
const DURACION_LLAMADO_MS = 13000;
const VENTANA_LLAMADO_MS = 3000;
const MAX_LLAMADOS_VISIBLES = 3;

// Clave única de un llamado (tipo de atención + número de turno)
const claveLlamado = (t) => `${t.codigoTipoAtencion}-${t.numeroTicket}`;

// Resuelve cuando el audio termina de sonar (o al llegar al respaldo), para
// poder reproducir los llamados uno detrás del otro sin encimarlos.
const esperarFinAudio = (audio, respaldoMs = 15000) => new Promise((resolve) => {
    let resuelto = false;
    const terminar = () => {
        if (resuelto) return;
        resuelto = true;
        audio.removeEventListener('ended', terminar);
        audio.removeEventListener('error', terminar);
        window.clearTimeout(timer);
        resolve();
    };
    const timer = window.setTimeout(terminar, respaldoMs);
    audio.addEventListener('ended', terminar);
    audio.addEventListener('error', terminar);
});

/**
 * CallScreen Component
 * Carga la misma lógica y sockets que Cola2.js pero con el diseño de pantalla completa
 * inspirado en Video.js (video de fondo a pantalla completa, lista translúcida flotante,
 * anuncio animado del llamado al centro y barra de texto en la parte inferior).
 * Se ejecuta por fuera de la administración ocultando el menú y el navbar.
 */
export const CallScreen = () => {
    // 1. Ocultar Menú y Navbar principal
    useHideMenu(true);

    // 2. Estados de formulario y entidad
    const [formData, setFormData] = useState({
        codigoEntidad: '',
        codigoUbicacion: ''
    });

    const { socket, conectarSocket } = useContext(SocketContext);

    const [entidadStorage, setEntidadStorage] = useState(
        localStorage.getItem('codigoEntidad') || null
    );
    const [ubicaciones, setUbicaciones] = useState([]);
    const [videoList, setVideoList] = useState(
        localStorage.getItem('videos') || ""
    );

    // 3. Estados de tickets, visibilidad y animaciones
    const [tickets, setTickets] = useState([]);
    const [minutosUltimo, setMinutosUltimo] = useState(0);

    // Cola de llamados visibles en el popup (permite mostrar 2 o 3 a la vez)
    const [calledTickets, setCalledTickets] = useState([]);
    const calledRef = useRef([]);
    const callTimersRef = useRef({});
    const ultimoLlamadoRef = useRef(0);

    // Estado del ciclo de la lista de turnos (10s visible, 5s oculto)
    const [showTicketsPanel, setShowTicketsPanel] = useState(true);

    // Estado para detectar soporte/bloqueo de sonido por políticas del navegador
    const [showAudioNotice, setShowAudioNotice] = useState(false);

    // Estado del mensaje marquee (sede → empresa → null)
    const [textoMarquee, setTextoMarquee] = useState(() => {
        const sede = localStorage.getItem('textoMarqueeSede') || '';
        const empresa = localStorage.getItem('textoMarqueeEmpresa') || '';
        return sede.trim() || empresa.trim() || null;
    });

    // Marca (nombre/logo) y sede para el encabezado superior
    const [marca, setMarca] = useState({ nombre: '', logo: '', icono: '' });
    const [nombreSede, setNombreSede] = useState('');
    // Posición dentro de la cadena de respaldos del logo del encabezado
    const [iconoIdx, setIconoIdx] = useState(0);

    // Reloj en vivo del encabezado
    const [ahora, setAhora] = useState(new Date());

    // Helper para ajustar dinámicamente la fuente si el texto es largo
    const getBadgeFontSize = (text, defaultSize = '1.25rem') => {
        if (!text) return defaultSize;
        const len = text.length;
        if (len > 35) return '0.8rem';
        if (len > 25) return '0.92rem';
        if (len > 18) return '1.05rem';
        return defaultSize;
    };

    // Reloj en vivo (encabezado superior)
    useEffect(() => {
        const timer = window.setInterval(() => setAhora(new Date()), 1000);
        return () => window.clearInterval(timer);
    }, []);

    // Al cambiar la marca de la entidad se reinicia la cadena de respaldos
    useEffect(() => { setIconoIdx(0); }, [marca.icono, marca.logo]);

    // Referencias reutilizables para el audio (un solo elemento y un solo AudioContext)
    const audioRef = useRef(null);
    const audioCtxRef = useRef(null);
    // Cola de audios: garantiza que los llamados suenen en orden, uno detrás del otro
    const audioQueueRef = useRef([]);
    const audioPlayingRef = useRef(false);

    const getAudio = useCallback(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio(sound);
            audioRef.current.preload = 'auto';
        }
        return audioRef.current;
    }, []);

    const ensureAudioCtx = useCallback(() => {
        if (!audioCtxRef.current) {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) {
                try {
                    audioCtxRef.current = new AudioCtx();
                } catch (err) {
                    console.warn("AudioContext creation warning:", err);
                }
            }
        }
        if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
            audioCtxRef.current.resume().catch(() => { });
        }
        return audioCtxRef.current;
    }, []);

    // Intento automático y silencioso de desbloquear contexto de audio
    const unlockAudio = useCallback(() => {
        // No interrumpir un llamado que se está reproduciendo
        if (audioPlayingRef.current) return;

        const audio = getAudio();
        ensureAudioCtx();

        try {
            audio.volume = 0.01;
            const p = audio.play();
            if (p !== undefined) {
                p.then(() => {
                    audio.pause();
                    audio.currentTime = 0;
                    setShowAudioNotice(false);
                }).catch(() => {
                    // Si el navegador bloquea la reproducción sin gesto previo
                    setShowAudioNotice(true);
                });
            }
        } catch (err) {
            console.warn("Autoplay audio check warning:", err);
        }
    }, [getAudio, ensureAudioCtx]);

    // Escuchar cualquier interacción o movimiento del usuario para desbloquear automáticamente
    useEffect(() => {
        const handleUserGesture = () => {
            unlockAudio();
            setShowAudioNotice(false);
            window.removeEventListener('click', handleUserGesture);
            window.removeEventListener('touchstart', handleUserGesture);
            window.removeEventListener('keydown', handleUserGesture);
            window.removeEventListener('mousemove', handleUserGesture);
        };

        window.addEventListener('click', handleUserGesture);
        window.addEventListener('touchstart', handleUserGesture);
        window.addEventListener('keydown', handleUserGesture);
        window.addEventListener('mousemove', handleUserGesture);

        unlockAudio();

        return () => {
            window.removeEventListener('click', handleUserGesture);
            window.removeEventListener('touchstart', handleUserGesture);
            window.removeEventListener('keydown', handleUserGesture);
            window.removeEventListener('mousemove', handleUserGesture);
        };
    }, [unlockAudio]);

    // Reintentos automáticos de desbloqueo por si el navegador permite autoplay
    // sin gesto (media engagement, dominio ya visitado, kiosko configurado para autoplay)
    useEffect(() => {
        let intentos = 0;
        const maxIntentos = 25;
        const timer = window.setInterval(() => {
            intentos += 1;
            unlockAudio();
            if (intentos >= maxIntentos) {
                window.clearInterval(timer);
            }
        }, 1500);

        return () => window.clearInterval(timer);
    }, [unlockAudio]);

    // Limpiar recursos de audio al desmontar
    useEffect(() => {
        return () => {
            if (audioCtxRef.current) {
                audioCtxRef.current.close().catch(() => { });
                audioCtxRef.current = null;
            }
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
                audioRef.current = null;
            }
            if (ttsUrlRef.current) {
                URL.revokeObjectURL(ttsUrlRef.current);
                ttsUrlRef.current = null;
            }
            audioQueueRef.current = [];
            audioPlayingRef.current = false;
        };
    }, []);

    // Reproducción segura del sonido (campana) sin romper React si el navegador
    // bloquea. Devuelve una promesa que resuelve cuando termina de sonar.
    const reproducirSonido = useCallback(() => {
        return new Promise((resolve) => {
            const audio = getAudio();
            ensureAudioCtx();

            const finalizar = () => {
                esperarFinAudio(audio, 5000).then(resolve);
            };

            try {
                audio.volume = 1;
                audio.currentTime = 0;
                const playPromise = audio.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            setShowAudioNotice(false);
                            finalizar();
                        })
                        .catch((err) => {
                            console.warn("Audio play prevenido por autoplay policy:", err);
                            setShowAudioNotice(true);
                            resolve();
                        });
                } else {
                    finalizar();
                }
            } catch (e) {
                console.warn("Error ejecutando sonido:", e);
                setShowAudioNotice(true);
                resolve();
            }
        });
    }, [getAudio, ensureAudioCtx]);

    // Función nativa para sintetizar voz con manejo de excepciones.
    // Devuelve una promesa que resuelve cuando termina de hablar.
    const decirTexto = useCallback((text) => {
        return new Promise((resolve) => {
            const synth = window.speechSynthesis;
            if (!synth) { resolve(); return; }

            let resuelto = false;
            const terminar = () => {
                if (resuelto) return;
                resuelto = true;
                resolve();
            };

            const ejecutarVoz = () => {
                try {
                    const voices = synth.getVoices();
                    const utterThis = new SpeechSynthesisUtterance(text);

                    const vocesLatinas = voices.filter(v =>
                        ['es-MX', 'es-CO', 'es-AR', 'es-CL', 'es-PE', 'es-US']
                            .includes(v.lang)
                    );

                    const vozFemenina = vocesLatinas.find(v =>
                        /paulina|helena|monica|maria|sofia|female/i.test(v.name)
                    );

                    const vozSeleccionada =
                        vozFemenina ||
                        vocesLatinas[0] ||
                        voices.find(v => v.lang.startsWith('es')) ||
                        voices[0];

                    utterThis.voice = vozSeleccionada;
                    utterThis.lang = vozSeleccionada?.lang || 'es-CO';
                    utterThis.rate = 0.85;
                    utterThis.pitch = 1;

                    utterThis.onend = terminar;
                    utterThis.onerror = (e) => {
                        console.warn("SpeechSynthesis error event:", e);
                        terminar();
                    };

                    if (synth.state === 'suspended') synth.resume();
                    synth.cancel();
                    synth.speak(utterThis);

                    // Respaldo por si el navegador no dispara onend
                    window.setTimeout(terminar, 15000);
                } catch (err) {
                    console.warn("Error en SpeechSynthesis:", err);
                    terminar();
                }
            };

            if (synth.getVoices().length > 0) {
                ejecutarVoz();
            } else {
                synth.onvoiceschanged = ejecutarVoz;
            }
        });
    }, []);

    // URL del último audio TTS para no acumular object URLs
    const ttsUrlRef = useRef(null);

    // Reproduce un llamado (campana o TTS). Devuelve una promesa que resuelve
    // cuando terminó de sonar, para poder encadenar los llamados.
    const reproducirLlamadoAudio = useCallback(async ({ mensaje, llamadoVoz, voz }) => {
        if (llamadoVoz !== 1) {
            await reproducirSonido();
            return;
        }

        try {
            const audio = getAudio();
            ensureAudioCtx();

            const resp = await serverApi.post(
                '/tickets/tts',
                {
                    mensaje,
                    voz: voz || '',
                    codigoEntidad: localStorage.getItem('codigoEntidad') || '',
                },
                { responseType: 'blob' }
            );

            if (!resp || !resp.data || !resp.data.size) {
                throw new Error('Respuesta TTS vacía');
            }

            if (ttsUrlRef.current) {
                URL.revokeObjectURL(ttsUrlRef.current);
            }

            const url = URL.createObjectURL(resp.data);
            ttsUrlRef.current = url;

            audio.src = url;
            audio.volume = 1;
            audio.currentTime = 0;

            const p = audio.play();
            if (p !== undefined) {
                try {
                    await p;
                    setShowAudioNotice(false);
                    await esperarFinAudio(audio);
                } catch (err) {
                    console.warn("Audio TTS bloqueado por autoplay:", err);
                    setShowAudioNotice(true);
                    await decirTexto(mensaje);
                }
            } else {
                await decirTexto(mensaje);
            }
        } catch (e) {
            const status = e.response?.status;
            if (status === 429) {
                console.warn("TTS rate limit alcanzado (429), usando voz local");
            } else if (!e.response && e.code) {
                console.warn(`TTS: conexión fallida (${e.code}), usando voz local`);
            } else if (status) {
                console.warn(`TTS: error servidor (${status}), usando voz local`);
            } else {
                console.warn("TTS: error desconocido, usando voz local:", e);
            }
            await decirTexto(mensaje);
        }
    }, [getAudio, ensureAudioCtx, decirTexto, reproducirSonido]);

    // Bombea la cola de audios: suena un llamado a la vez, en orden de llegada
    const bombearColaAudio = useCallback(async () => {
        if (audioPlayingRef.current) return;

        audioPlayingRef.current = true;
        try {
            while (audioQueueRef.current.length > 0) {
                const siguiente = audioQueueRef.current.shift();
                try {
                    await reproducirLlamadoAudio(siguiente);
                } catch (e) {
                    console.warn("Error reproduciendo audio de llamado:", e);
                }
            }
        } finally {
            audioPlayingRef.current = false;
        }
    }, [reproducirLlamadoAudio]);

    // Encola el audio del llamado. Si ya hay uno sonando, el siguiente espera
    // a que el actual termine y recién ahí se reproduce.
    const hablarLlamado = useCallback((mensaje, llamadoVoz, voz) => {
        audioQueueRef.current.push({ mensaje, llamadoVoz, voz });
        bombearColaAudio();
    }, [bombearColaAudio]);

    // Temporizador para el ciclo de visibilidad del panel (10 segundos visible / 5 segundos oculto)
    useEffect(() => {
        let timer;
        if (showTicketsPanel) {
            timer = setTimeout(() => {
                setShowTicketsPanel(false);
            }, 10000); // Muestra por 10s
        } else {
            timer = setTimeout(() => {
                setShowTicketsPanel(true);
            }, 5000); // Ocúltalo por 5s
        }
        return () => clearTimeout(timer);
    }, [showTicketsPanel]);

    // Forzar la aparición inmediata del panel cuando llega un llamado
    const forzarMostrarPanel = useCallback(() => {
        setShowTicketsPanel(true);
    }, []);

    // Agrega un llamado al popup. Si llega dentro de VENTANA_LLAMADO_MS del
    // anterior se muestra junto a él (hasta MAX_LLAMADOS_VISIBLES); si llega
    // más tarde, reemplaza al grupo anterior. Cada tarjeta se retira sola tras
    // DURACION_LLAMADO_MS.
    const mostrarLlamado = useCallback((t) => {
        const key = claveLlamado(t);
        const ahoraMs = Date.now();
        const dentroDeVentana = !!ultimoLlamadoRef.current &&
            (ahoraMs - ultimoLlamadoRef.current) <= VENTANA_LLAMADO_MS;

        const previos = calledRef.current;
        const yaVisible = previos.some((x) => claveLlamado(x) === key);
        const base = (dentroDeVentana || yaVisible) ? previos : [];

        const siguiente = [t, ...base.filter((x) => claveLlamado(x) !== key)]
            .slice(0, MAX_LLAMADOS_VISIBLES);

        // Descartar temporizadores de las tarjetas que salen de pantalla
        const keysSiguiente = new Set(siguiente.map(claveLlamado));
        Object.keys(callTimersRef.current).forEach((k) => {
            if (!keysSiguiente.has(k)) {
                clearTimeout(callTimersRef.current[k]);
                delete callTimersRef.current[k];
            }
        });

        calledRef.current = siguiente;
        setCalledTickets(siguiente);
        ultimoLlamadoRef.current = ahoraMs;

        if (callTimersRef.current[key]) {
            clearTimeout(callTimersRef.current[key]);
        }
        callTimersRef.current[key] = setTimeout(() => {
            const actuales = calledRef.current.filter((x) => claveLlamado(x) !== key);
            calledRef.current = actuales;
            setCalledTickets(actuales);
            delete callTimersRef.current[key];
        }, DURACION_LLAMADO_MS);
    }, []);

    // Limpiar temporizadores de llamados al desmontar
    useEffect(() => {
        const timers = callTimersRef.current;
        return () => {
            Object.values(timers).forEach((id) => clearTimeout(id));
        };
    }, []);

    // Ayuda de depuración (solo desarrollo): permite simular llamados desde la
    // consola del navegador con window.__llamadoSocket.onevent(...)
    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') {
            window.__llamadoSocket = socket;
        }
        return () => {
            if (window.__llamadoSocket === socket) {
                delete window.__llamadoSocket;
            }
        };
    }, [socket]);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        if (e.target.id === 'codigoEntidad') {
            setUbicaciones([]);
            setFormData((prev) => ({
                ...prev,
                codigoEntidad: newValue,
                codigoUbicacion: '',
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [e.target.id]: newValue
            }));
        }
    };

    const fetchUbicaciones = useCallback(async (codigo) => {
        if (!codigo || codigo.trim().length < 3) return;
        try {
            const resp = await serverApi.post(
                `/auth/select-ubicacion-pantallas`,
                { codigoEntidad: codigo.trim() }
            );

            if (!resp.data.ubicaciones || resp.data.ubicaciones.length === 0) return;

            const data = resp.data.ubicaciones.map((item) => ({
                value: item.codigoUbicacion,
                label: item.nombreUbicacion
            }));

            setUbicaciones(data);
        } catch (error) {
            console.error("Error al obtener ubicaciones", error);
        }
    }, []);

    useEffect(() => {
        if (formData.codigoEntidad && formData.codigoEntidad.trim().length >= 3) {
            fetchUbicaciones(formData.codigoEntidad.trim());
        }
    }, [formData.codigoEntidad, fetchUbicaciones]);

    const parseVideos = (videosRaw) => parseVideosLista(videosRaw);

    // Resuelve la URL absoluta del logo/ícono de la entidad
    const urlRecurso = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${serverApi.defaults.baseURL.replace('/api', '')}${path}`;
    };

    const ingresar = async () => {
        if (formData.codigoUbicacion === "" || formData.codigoEntidad === "") {
            return UseSweetAlert("Atención!", 'Falta información', 'error');
        }

        const resp = await serverApi.post(
            `/auth/registro-pantallas`,
            formData
        );

        const pantallas = resp.data.pantallas;
        if (!pantallas || pantallas.length === 0) {
            return UseSweetAlert("Atención!", "Revise la información", "error");
        }

        const pantalla = pantallas[0];
        const listaVideos = parseVideos(pantalla.videos);

        localStorage.setItem('codigoEntidad', formData.codigoEntidad);
        localStorage.setItem('codigoUbicacion', formData.codigoUbicacion);
        localStorage.setItem('llamadoVoz', pantalla.llamadoVoz);
        localStorage.setItem('videos', listaVideos);
        localStorage.setItem('textoMarqueeEmpresa', pantalla.textoMarquee || '');
        localStorage.setItem('textoMarqueeSede', pantalla.textoMarqueeSede || '');

        const marqueeSede = (pantalla.textoMarqueeSede || '').trim();
        const marqueeEmpresa = (pantalla.textoMarquee || '').trim();
        setTextoMarquee(marqueeSede || marqueeEmpresa || null);

        // Marca / sede para el encabezado
        setMarca({
            nombre: pantalla.nombreEntidad || '',
            logo: pantalla.imagenLogo || '',
            icono: pantalla.imagenIcono || '',
        });
        guardarColoresStorage(pantalla.colorPrimario, pantalla.colorSecundario);
        const ubicacionSel = ubicaciones.find(u => u.value === formData.codigoUbicacion);
        setNombreSede(ubicacionSel ? ubicacionSel.label : '');

        conectarSocket({
            query: {
                codigoEntidad: formData.codigoEntidad,
                ubicacion: formData.codigoUbicacion,
            }
        });

        setEntidadStorage(formData.codigoEntidad);
        setVideoList(listaVideos);

        const tks = await getUltimos();
        setTickets(tks ?? []);
    };

    // Cargar lista de videos actualizada
    useEffect(() => {
        const fetchVideos = async () => {
            const codigoEntidad = localStorage.getItem("codigoEntidad");
            const codigoUbicacion = localStorage.getItem("codigoUbicacion");

            console.log("entidadStorage");
            console.log(entidadStorage);
            if (!entidadStorage) return;



            try {
                const resp = await serverApi.post(
                    `/videos/lista-videos-mostrar`,
                    { codigoEntidad, codigoUbicacion }
                );

                console.log(resp);

                if (resp.data.videos && resp.data.videos.length > 0) {
                    const listaVideos = parseVideosLista(resp.data.videos);
                    localStorage.setItem('videos', listaVideos);
                    setVideoList(listaVideos);
                }
            } catch (error) {
                console.error("Error cargando lista de videos:", error);
            }
        };

        fetchVideos();
    }, [entidadStorage]);

    // Cargar marca (logo/nombre), sede y mensaje marquee cuando el kiosko
    // arranca con una sesión previa en localStorage
    useEffect(() => {
        const fetchMarca = async () => {
            const codigoEntidad = localStorage.getItem("codigoEntidad");
            const codigoUbicacion = localStorage.getItem("codigoUbicacion");
            if (!entidadStorage) return;
            try {
                const [respPantalla, respUbis] = await Promise.all([
                    serverApi.post(
                        `/auth/registro-pantallas`,
                        { codigoEntidad, codigoUbicacion }
                    ),
                    serverApi.post(
                        `/auth/select-ubicacion-pantallas`,
                        { codigoEntidad }
                    ),
                ]);

                const pantallas = respPantalla.data.pantallas;
                if (pantallas && pantallas.length > 0) {
                    const pantalla = pantallas[0];
                    const marqueeSede = (pantalla.textoMarqueeSede || '').trim();
                    const marqueeEmpresa = (pantalla.textoMarquee || '').trim();
                    setTextoMarquee(marqueeSede || marqueeEmpresa || null);
                    setMarca({
                        nombre: pantalla.nombreEntidad || '',
                        logo: pantalla.imagenLogo || '',
                        icono: pantalla.imagenIcono || '',
                    });
                    guardarColoresStorage(pantalla.colorPrimario, pantalla.colorSecundario);
                }

                const libs = respUbis.data.ubicaciones || [];
                const ubicacion = libs.find(u => u.codigoUbicacion === codigoUbicacion);
                if (ubicacion) setNombreSede(ubicacion.nombreUbicacion);
            } catch (error) {
                console.error("Error cargando datos de la pantalla:", error);
            }
        };
        fetchMarca();
    }, [entidadStorage]);

    // Manejo de eventos Sockets
    useEffect(() => {
        if (!socket) return;

        const handleAsignados = (ticketsList) => {
            if (!ticketsList || ticketsList.length === 0) return;
            const t = ticketsList[0];

            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);

            setTickets(ticketsList ?? []);
            mostrarLlamado(t);
            forzarMostrarPanel(); // Reaparece el panel si estaba oculto

            const paciente = t.nombrePaciente?.trim() ||
                `Turno ${t.codigoTipoAtencion} ${t.numeroTicket}`;
            const mensaje = `${paciente}, pase a ${t.moduloClase} ${t.modulo}`;

            hablarLlamado(mensaje, t.llamadoVoz, t.voz);
        };

        const handleVolverLlamar = (ticketList) => {
            if (!ticketList || ticketList.length === 0) return;
            const t = ticketList[0];

            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);

            mostrarLlamado(t);
            forzarMostrarPanel(); // Reaparece el panel si estaba oculto

            setTickets(prev => {
                const filtered = prev.filter(item => !(item.numeroTicket === t.numeroTicket && item.codigoTipoAtencion === t.codigoTipoAtencion));
                return [t, ...filtered];
            });

            const paciente = t.nombrePaciente?.trim() ||
                `Turno ${t.codigoTipoAtencion} ${t.numeroTicket}`;
            const mensaje = `${paciente}, pase a ${t.moduloClase} ${t.modulo}`;

            hablarLlamado(mensaje, t.llamadoVoz, t.voz);
        };

        const handleListar = (ticketsList) => {
            if (!ticketsList || ticketsList.length === 0) {
                setTickets([]);
                return;
            }
            const t = ticketsList[0];
            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);

            setTickets(ticketsList ?? []);
        };

        socket.on('ticket-asignado', handleAsignados);
        socket.on('volver-llamar', handleVolverLlamar);
        socket.on('ticket-listado-13', handleListar);

        return () => {
            socket.off('ticket-asignado', handleAsignados);
            socket.off('volver-llamar', handleVolverLlamar);
            socket.off('ticket-listado-13', handleListar);
        };
    }, [socket, hablarLlamado, forzarMostrarPanel, mostrarLlamado]);

    // Carga inicial
    useEffect(() => {
        const load = async () => {
            const tks = await getUltimos();
            if (!tks || tks.length === 0) return;
            const t = tks[0];

            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);
            setTickets(tks ?? []);
        };
        load();
    }, []);

    // Formato de reloj/fecha (es-CO) e ícono para el encabezado
    const horaTexto = ahora.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
    const fechaTexto = ahora.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' });

    // El hueco del logo en el encabezado es cuadrado, por eso se prefiere el
    // ícono (isotipo); el logo completo queda como respaldo si el ícono falta
    // o está roto. Si ambos fallan se muestra el ícono genérico del hospital.
    const logoCandidatos = [
        urlRecurso(marca.icono),
        urlRecurso(marca.logo),
    ].filter((v, i, arr) => v && arr.indexOf(v) === i);
    const logoSrc = logoCandidatos[iconoIdx] || '';

    return (
        <>
            {entidadStorage !== null ? (
                <div className="callscreen-wrapper">
                    {/* Adornos de fondo suaves (tema claro) */}
                    <div className="callscreen-glow callscreen-glow-a" />
                    <div className="callscreen-glow callscreen-glow-b" />

                    {/* Notice flotante discreto si el navegador bloquea audio antes de cualquier movimiento */}
                    {showAudioNotice && (
                        <div
                            className="callscreen-interaction-notice"
                            onClick={() => {
                                unlockAudio();
                                setShowAudioNotice(false);
                            }}
                        >
                            <i className="fa-solid fa-volume-high"></i>
                            <span>Toca en cualquier lugar para habilitar el sonido de los llamados</span>
                        </div>
                    )}

                    {/* 0. Encabezado superior: marca, sede y reloj en vivo */}
                    <header className="callscreen-topbar">
                        <div className="callscreen-topbar-entity">
                            {logoSrc ? (
                                <span className="callscreen-topbar-logo">
                                    <img
                                        src={logoSrc}
                                        alt="Ícono"
                                        onError={() => setIconoIdx(i => i + 1)}
                                    />
                                </span>
                            ) : (
                                <span className="callscreen-topbar-logo callscreen-topbar-logo-placeholder">
                                    <i className="fa-solid fa-hospital"></i>
                                </span>
                            )}
                            <div className="callscreen-topbar-text">
                                <span className="callscreen-topbar-name">
                                    {marca.nombre || 'Pantalla de llamado'}
                                </span>
                                {nombreSede && (
                                    <span className="callscreen-topbar-sede">
                                        <i className="fa-solid fa-location-dot"></i> {nombreSede}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="callscreen-topbar-clock">
                            <span className="callscreen-clock-time">{horaTexto}</span>
                            <span className="callscreen-clock-date">{fechaTexto}</span>
                        </div>
                    </header>

                    {/* 1. Fondo de Video a pantalla completa (solo si hay videos) */}
                    {videoList && (
                        <div className="callscreen-video-bg">
                            <VideoPlayer videos={videoList} />
                        </div>
                    )}

                    {/* Vignette translúcido (solo si hay video de fondo) */}
                    {videoList && <div className="callscreen-vignette" />}

                    {/* 2. Panel Flotante Transparente / Glassmorphism de la Lista de Turnos */}
                    <div className={`callscreen-tickets-panel ${!showTicketsPanel ? 'callscreen-tickets-panel-hidden' : ''}`}>
                        <div className="callscreen-tickets-header">
                            <div>Turno</div>
                            <div>Módulo</div>
                        </div>

                        <div className="callscreen-tickets-list">
                            {tickets.slice(0, 6).map((ticket, index) => {
                                const textoModulo = `${ticket.moduloClase} ${ticket.modulo}`;
                                const isLatestCall = index === 0 && minutosUltimo < 0.10;
                                const textoTurno = ticket.nombrePaciente && ticket.nombrePaciente.trim() !== ""
                                    ? `${ticket.codigoTipoAtencion}-${ticket.numeroTicket} ${ticket.nombrePaciente}`
                                    : `${ticket.codigoTipoAtencion}-${ticket.numeroTicket} ${ticket.numeroDocumento || ''}`;

                                return (
                                    <div
                                        key={index}
                                        className={`callscreen-ticket-row ${isLatestCall ? 'callscreen-row-active parpadea-futuristic' : ''}`}
                                    >
                                        <div
                                            className="callscreen-badge-turno"
                                            style={{ fontSize: getBadgeFontSize(textoTurno, '1.25rem') }}
                                            title={textoTurno}
                                        >
                                            {textoTurno}
                                        </div>
                                        <div
                                            className="callscreen-badge-modulo"
                                            style={{ fontSize: getBadgeFontSize(textoModulo, '1.15rem') }}
                                            title={textoModulo}
                                        >
                                            {textoModulo}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* 3. Popup del/los turnos llamados al centro (soporta 2+ a la vez) */}
                    {calledTickets.length > 0 && (
                        <div className={`callscreen-overlay-center ${calledTickets.length > 1 ? 'callscreen-overlay-center--multi' : ''}`}>
                            {calledTickets.map((t, index) => (
                                <div
                                    key={`${t.codigoTipoAtencion}-${t.numeroTicket}-${index}`}
                                    className="callscreen-overlay-card parpadea-futuristic"
                                >
                                    <div className="callscreen-overlay-label">Llamando AHORA</div>
                                    <div className="callscreen-overlay-turno">
                                        {t.codigoTipoAtencion}-{t.numeroTicket}
                                    </div>
                                    <div className="callscreen-overlay-paciente">
                                        {t.nombrePaciente && t.nombrePaciente.trim() !== "" ? t.nombrePaciente : ''}
                                    </div>
                                    <div className="callscreen-overlay-modulo-badge">
                                        {t.moduloClase} {t.modulo}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 4. Marquee de información en la parte inferior */}
                    {textoMarquee && (
                        <div className="callscreen-marquee-container">
                            <Marquee mensaje={textoMarquee} />
                        </div>
                    )}
                </div>
            ) : (
                /* Pantalla de login/configuración de la pantalla si no hay entidad configurada */
                <div className="login-container d-flex align-items-center justify-content-center p-3" style={{ minHeight: "100vh", backgroundColor: '#f4f7f6' }}>
                    <div className="col-12 col-md-6 col-lg-4">
                        <div className="card shadow-lg border-0 rounded-4 overflow-hidden" style={{ backgroundColor: '#ffffff' }}>
                            <div className="p-4 text-white text-center" style={{ background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-primario-oscuro) 100%)' }}>
                                <div className="d-inline-flex align-items-center justify-content-center mb-2 rounded-circle" style={{ width: 56, height: 56, background: 'rgba(255,255,255,0.15)' }}>
                                    <i className="fa-solid fa-tv fs-3" style={{ color: 'var(--color-secundario)' }}></i>
                                </div>
                                <h4 className="fw-bold m-0" style={{ letterSpacing: '0.5px' }}>Pantalla de Llamado (CallScreen)</h4>
                                <p className="mb-0 opacity-75 small">Configuración de Visualización de Turnos</p>
                            </div>

                            <div className="p-4">
                                <div className="mb-3">
                                    <label htmlFor="codigoEntidad" className="form-label small fw-bold text-secondary">Código de Entidad / Empresa</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="fa-solid fa-building" style={{ color: 'var(--color-primario-texto)' }}></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0 ps-0 shadow-none"
                                            id="codigoEntidad"
                                            placeholder="Ej: femcc-01"
                                            value={formData.codigoEntidad}
                                            onChange={handleInputChange}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>

                                {ubicaciones.length > 0 && (
                                    <div className='mb-4'>
                                        <label htmlFor="codigoUbicacion" className="form-label small fw-bold text-secondary">Sede / Ubicación</label>
                                        <SelectElement
                                            options={ubicaciones}
                                            id={"codigoUbicacion"}
                                            name={"codigoUbicacion"}
                                            isMulti={false}
                                            closeOnMenuSelect={true}
                                            className={"shadow-sm rounded-3"}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                )}

                                {formData.codigoUbicacion && (
                                    <button
                                        className="btn w-100 py-3 fw-bold text-white shadow-sm rounded-3 transition-all mt-2"
                                        type="button"
                                        onClick={ingresar}
                                        style={{ background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-primario-oscuro) 100%)', border: 'none' }}
                                    >
                                        INGRESAR A LA PANTALLA →
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CallScreen;
